import { effect, inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  combineLatest,
  debounceTime,
  firstValueFrom,
  map,
  of,
  OperatorFunction,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { PagedResult, PagingOptions } from 'src/app/shared/models/paging';
import { Organization, OrganizationDisplay, OrganizationSearchEvent } from '../../organization-models';
import { ActivatedRoute, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import OrganizationService from 'src/app/shared/services/organization.service';
import { translateOrganizationType } from 'src/app/shared/mappers/enum-translation-mapper';
import { ExpandListSort, SnackbarService } from 'ok-angular-shared/components';

export interface FilterState {
  query: string | null;
  page: PageEvent;
  sort: Sort | null;
  organizationId: number | null;
  departmentId: number | null;
  kickbackId: number | null;
  newlyCreatedOrganizationId?: number;
}

export enum OrganizationQueryParams {
  query = 'query',
  organizationId = 'organizationId',
  departmentId = 'departmentId',
  kickbackId = 'kickbackId',
}

@Injectable()
export class OrganizationListStateService extends PagerBase {
  private activatedRoute = inject(ActivatedRoute);
  private organizationService = inject(OrganizationService);
  private snackbarService = inject(SnackbarService);
  private router = inject(Router);
  public highlightDepartmentId: number | null = null;
  public refresh$ = new Subject<void>();
  public loading = signal<boolean>(false);
  public pagingOptions: PagingOptions = {
    pageSize: this.defaultPageSize,
    pageSizeOptions: this.pageSizes,
  };
  public currentSort = signal<ExpandListSort<OrganizationDisplay>>({ column: 'name', direction: 'asc' });
  private mapOperator$: OperatorFunction<PagedResult<Organization>, PagedResult<OrganizationDisplay>>;
  private errorOperatorDisplay$: OperatorFunction<PagedResult<OrganizationDisplay>, PagedResult<OrganizationDisplay>>;

  focusedOrganization = signal<Organization | null>(null);

  public organizationsState = signal<PagedResult<OrganizationDisplay>>({
    items: [],
    totalCount: 0,
    pageIndex: 0,
    pageSize: this.pagingOptions.pageSize,
  });

  public refreshSingleOrganization$ = new Subject<number>();

  readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);

  public filters = signal<FilterState>({
    organizationId: this.queryParamMap$()?.get(OrganizationQueryParams.organizationId)
      ? parseInt(this.queryParamMap$()!.get(OrganizationQueryParams.organizationId)!)
      : null,
    departmentId: this.queryParamMap$()?.get(OrganizationQueryParams.departmentId)
      ? parseInt(this.queryParamMap$()!.get(OrganizationQueryParams.departmentId)!)
      : null,
    kickbackId: this.queryParamMap$()?.get(OrganizationQueryParams.kickbackId)
      ? parseInt(this.queryParamMap$()!.get(OrganizationQueryParams.kickbackId)!)
      : null,
    query: this.queryParamMap$()?.get(OrganizationQueryParams.query)
      ? decodeURIComponent(this.queryParamMap$()!.get(OrganizationQueryParams.query)!)
      : null,
    sort: null,
    page: {
      length: 0,
      pageIndex: 0,
      pageSize: this.pagingOptions.pageSize,
    },
    newlyCreatedOrganizationId: undefined,
  });

  constructor() {
    super();

    this.errorOperatorDisplay$ = catchError(() => {
      this.loading.set(false);
      this.snackbarService.error('Der opstod en ukendt fejl ved hentning af organisationer');
      return of({
        items: [],
        totalCount: 0,
        pageIndex: 0,
        pageSize: this.pagingOptions.pageSize,
      });
    });

    this.mapOperator$ = map<PagedResult<Organization>, PagedResult<OrganizationDisplay>>(result => {
      const mappedItems = result.items.map(
        organization =>
          <OrganizationDisplay>{
            ...organization,
            isExpanded: organization.id === this.filters().newlyCreatedOrganizationId,
            highlight: organization.id === this.filters().organizationId,
            enabled: true, // Add the missing enabled property
            translatedOrganizationType: translateOrganizationType(organization.organizationType),
          }
      );
      return {
        ...result,
        items: mappedItems,
      };
    });

    combineLatest([toObservable(this.filters), this.refresh$.pipe(startWith(void 0))])
      .pipe(
        debounceTime(300),
        tap(() => this.loading.set(true)),
        switchMap(async ([filters]) => {
          if (filters.organizationId) {
            const focusedOrganization = await firstValueFrom(this.organizationService.getOrganization(filters.organizationId));
            this.focusedOrganization.set(focusedOrganization);
            const result: PagedResult<OrganizationDisplay> = {
              items: [
                {
                  ...focusedOrganization,
                  isExpanded: false,
                  highlight: true,
                  enabled: true,
                  translatedOrganizationType: translateOrganizationType(focusedOrganization.organizationType),
                },
              ],
              totalCount: 1,
              pageIndex: 0,
              pageSize: this.pagingOptions.pageSize,
            };
            return result;
          }

          // Clear focused organization when no organizationId filter
          this.focusedOrganization.set(null);
          const organizationSearchEvent: OrganizationSearchEvent = {
            paging: filters.page,
            sorting: filters.sort,
            pageIndex: 0,
            pageSize: this.pagingOptions.pageSize,
            query: filters.query,
          } as OrganizationSearchEvent;

          return firstValueFrom(
            this.organizationService.getOrganizations(organizationSearchEvent).pipe(this.mapOperator$, this.errorOperatorDisplay$)
          );
        }),
        tap(result => {
          this.organizationsState.set(result);
          this.loading.set(false);
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    this.refreshSingleOrganization$
      .pipe(
        switchMap(organizationId => {
          return this.organizationService.getOrganization(organizationId).pipe(
            tap(organization => {
              const itemIndex = this.organizationsState().items.findIndex(s => s.id === organizationId);
              const updatedOrg = {
                ...organization,
                isExpanded: true,
                enabled: true,
                translatedOrganizationType: translateOrganizationType(organization.organizationType),
              } as OrganizationDisplay;
              this.organizationsState.update(current => {
                const newItems = [...current.items];
                //replace item in list with new one
                newItems[itemIndex] = {
                  ...updatedOrg,
                };
                return {
                  ...current,
                  items: newItems,
                };
              });
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    effect(() => {
      const filters = this.filters();
      const queryParams: Record<string, string | undefined> = {};

      // Only add non-null parameters to avoid null values in URL
      if (filters.organizationId) {
        queryParams[OrganizationQueryParams.organizationId] = filters.organizationId.toString();
      }
      if (filters.departmentId) {
        queryParams[OrganizationQueryParams.departmentId] = filters.departmentId.toString();
      }
      if (filters.query && filters.query.trim()) {
        queryParams[OrganizationQueryParams.query] = encodeURIComponent(filters.query);
      }

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        replaceUrl: true,
      });
    });

    // Watch for external URL changes (like from search overlay) using route params directly
    effect(() => {
      const queryParamMap = this.queryParamMap$();
      if (queryParamMap) {
        const urlOrganizationId = queryParamMap.get(OrganizationQueryParams.organizationId)
          ? parseInt(queryParamMap.get(OrganizationQueryParams.organizationId)!)
          : null;

        // Only update from URL when organizationId changes (from external navigation like search overlay)
        const currentFilters = this.filters();
        if (currentFilters.organizationId !== urlOrganizationId && urlOrganizationId !== null) {
          this.filters.update(f => ({
            ...f,
            organizationId: urlOrganizationId,
            departmentId: null,
            query: null,
          }));
        }
      }
    });
  }

  public resetFilter() {
    this.focusedOrganization.set(null);

    // Directly clear the URL first
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      replaceUrl: true,
    });

    // Then update the filters
    this.filters.update(f => ({
      ...f,
      page: { length: 0, pageIndex: 0, pageSize: this.pagingOptions.pageSize },
      departmentId: null,
      organizationId: null,
      query: null,
      kickbackId: null,
      newlyCreatedOrganizationId: undefined,
    }));
  }

  public onSortChange(sort: ExpandListSort<OrganizationDisplay>) {
    this.loading.set(true);
    this.currentSort.set(sort);

    // Convert ExpandListSort to Material Sort format and update filters
    const matSort: Sort = {
      active: sort.column as string,
      direction: sort.direction,
    };

    // Update filters to reset to first page when sorting and include sort data
    this.filters.update(f => ({
      ...f,
      sort: matSort,
      page: {
        length: f.page.length,
        pageIndex: 0,
        pageSize: f.page.pageSize,
      },
    }));
  }
}
